// VIBRANIAM-SPACE v2 - useAudioEngine.js
// Web Audio input (microphone / file / test tone) feeding the WASM FFT
// analyzer. One analysis frame is pulled per rendered frame.

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadAudioAnalyzer } from '../engine/wasm.js';
import { EMPTY_FEATURES } from '../engine/evaluate.js';

export function useAudioEngine({ fftSize = 2048, bands = 32, sensitivity = 1.5 } = {}) {
  const [status, setStatus] = useState('idle'); // idle | loading | running | error
  const [error, setError] = useState(null);
  const [sourceLabel, setSourceLabel] = useState('nessuna sorgente');

  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const wasmRef = useRef(null);
  const timeDataRef = useRef(new Float32Array(fftSize));
  const chainRef = useRef({ nodes: [], stream: null });
  const featuresRef = useRef({ ...EMPTY_FEATURES });
  const lastTickRef = useRef(0);

  // Camera source state. When active, the viewport pointer becomes a proxy for
  // the camera: position drives the spectrum shape, velocity drives energy.
  const cameraModeRef = useRef(false);
  const cameraRef = useRef({ x: 0, y: 0, vx: 0, vy: 0, last: 0 });

  const teardownChain = useCallback(() => {
    const { nodes, stream } = chainRef.current;
    nodes.forEach((n) => { try { n.disconnect(); } catch { /* già scollegato */ } });
    nodes.forEach((n) => { try { n.stop && n.stop(); } catch { /* non è una sorgente */ } });
    if (stream) stream.getTracks().forEach((t) => t.stop());
    chainRef.current = { nodes: [], stream: null };
    cameraModeRef.current = false;
  }, []);

  const ensureContext = useCallback(async () => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new Ctx();
    }
    if (ctxRef.current.state === 'suspended') await ctxRef.current.resume();

    if (!analyserRef.current) {
      const a = ctxRef.current.createAnalyser();
      a.fftSize = fftSize;
      a.smoothingTimeConstant = 0;
      analyserRef.current = a;
      timeDataRef.current = new Float32Array(a.fftSize);
    }

    if (!wasmRef.current) {
      const mod = await loadAudioAnalyzer();
      const analyzer = new mod.AudioAnalyzer(fftSize, ctxRef.current.sampleRate);
      analyzer.setBandCount(bands);
      analyzer.setOnsetSensitivity(sensitivity);
      wasmRef.current = { mod, analyzer };
    }
    return ctxRef.current;
  }, [fftSize, bands, sensitivity]);

  const startMic = useCallback(async () => {
    setStatus('loading'); setError(null);
    try {
      const ctx = await ensureContext();
      teardownChain();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const src = ctx.createMediaStreamSource(stream);
      src.connect(analyserRef.current);
      chainRef.current = { nodes: [src], stream };
      setSourceLabel('microfono');
      setStatus('running');
      return true;
    } catch (err) {
      setError(err.message); setStatus('error'); return false;
    }
  }, [ensureContext, teardownChain]);

  const startFile = useCallback(async (file) => {
    setStatus('loading'); setError(null);
    try {
      const ctx = await ensureContext();
      teardownChain();
      const buf = await ctx.decodeAudioData(await file.arrayBuffer());
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 1;
      src.connect(gain);
      gain.connect(analyserRef.current);
      gain.connect(ctx.destination);
      src.start();
      chainRef.current = { nodes: [src, gain], stream: null };
      setSourceLabel(`${file.name} (${buf.duration.toFixed(1)}s)`);
      setStatus('running');
      return true;
    } catch (err) {
      setError(err.message); setStatus('error'); return false;
    }
  }, [ensureContext, teardownChain]);

  const startTestTone = useCallback(async () => {
    setStatus('loading'); setError(null);
    try {
      const ctx = await ensureContext();
      teardownChain();

      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(110, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 8);

      const lfo = ctx.createOscillator();
      lfo.type = 'square';
      lfo.frequency.value = 2;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.45;

      const gate = ctx.createGain();
      gate.gain.value = 0.5;
      lfo.connect(lfoGain);
      lfoGain.connect(gate.gain);

      osc.connect(gate);
      gate.connect(analyserRef.current);

      osc.start(); lfo.start();
      chainRef.current = { nodes: [osc, lfo, lfoGain, gate], stream: null };
      setSourceLabel('test tone (sweep + gate)');
      setStatus('running');
      return true;
    } catch (err) {
      setError(err.message); setStatus('error'); return false;
    }
  }, [ensureContext, teardownChain]);

  const startCamera = useCallback(() => {
    setStatus('loading'); setError(null);
    teardownChain();
    cameraModeRef.current = true;
    cameraRef.current = { x: 0.5, y: 0.5, vx: 0, vy: 0, last: 0 };
    setSourceLabel('camera');
    setStatus('running');
    return true;
  }, [teardownChain]);

  const stop = useCallback(() => {
    teardownChain();
    cameraModeRef.current = false;
    featuresRef.current = { ...EMPTY_FEATURES };
    setSourceLabel('nessuna sorgente');
    setStatus('idle');
  }, [teardownChain]);

  /** Pull one analysis frame. Call once per rendered frame. */
  const tick = useCallback((now = performance.now()) => {
    const w = wasmRef.current;
    const a = analyserRef.current;
    if (status !== 'running') return featuresRef.current;

    const dt = lastTickRef.current ? Math.min(0.1, (now - lastTickRef.current) / 1000) : 1 / 60;
    lastTickRef.current = now;

    // Camera source: synthesise an analysis frame from the viewport pointer
    // instead of the FFT. Position maps to the spectral bands, velocity to
    // energy (rms) and flux.
    if (cameraModeRef.current) {
      const c = cameraRef.current;
      const speed = Math.min(1, Math.hypot(c.vx, c.vy));
      const n = bands;
      const outBands = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        const t = n > 1 ? i / (n - 1) : 0;
        const band = Math.sin(t * Math.PI * (0.5 + c.x)) * 0.5 + 0.5;
        outBands[i] = band * speed;
      }
      const rms = speed * 0.8;
      const bass = outBands.slice(0, Math.max(1, Math.floor(n / 3))).reduce((s, v) => s + v, 0) / Math.max(1, Math.floor(n / 3));
      const mid = outBands.slice(Math.floor(n / 3), Math.floor((2 * n) / 3)).reduce((s, v) => s + v, 0) / Math.max(1, Math.floor(n / 3));
      const high = outBands.slice(Math.floor((2 * n) / 3)).reduce((s, v) => s + v, 0) / Math.max(1, n - Math.floor((2 * n) / 3));
      featuresRef.current = {
        ...EMPTY_FEATURES,
        bands: outBands,
        spectrum: outBands,
        rms,
        peak: rms,
        flux: speed,
        onset: speed > 0.6,
        bass, mid, high,
        binCount: n,
      };
      // Damp velocity so a still pointer decays toward silence.
      c.vx *= 0.88; c.vy *= 0.88;
      return featuresRef.current;
    }

    if (!w || !a) return featuresRef.current;

    if (timeDataRef.current.length !== a.fftSize) {
      timeDataRef.current = new Float32Array(a.fftSize);
    }
    a.getFloatTimeDomainData(timeDataRef.current);
    featuresRef.current = w.analyzer.analyze(timeDataRef.current, dt);
    return featuresRef.current;
  }, [status, bands]);

  const getFeatures = useCallback(() => featuresRef.current, []);

  useEffect(() => {
    const w = wasmRef.current;
    if (w) {
      w.analyzer.setBandCount(bands);
      w.analyzer.setOnsetSensitivity(sensitivity);
    }
  }, [bands, sensitivity]);

  // Camera source pointer tracking: only active while camera mode is on. The
  // pointer position/velocity over the whole window drives the synthesised
  // analysis frame returned by tick().
  useEffect(() => {
    if (!cameraModeRef.current) return undefined;
    const onMove = (e) => {
      const now = performance.now();
      const x = e.clientX / Math.max(1, window.innerWidth);
      const y = e.clientY / Math.max(1, window.innerHeight);
      const c = cameraRef.current;
      const dt = c.last ? Math.min(0.1, (now - c.last) / 1000) : 0;
      if (dt > 0) {
        c.vx = (x - c.x) / dt;
        c.vy = (y - c.y) / dt;
      }
      c.x = x; c.y = y; c.last = now;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [status]);

  useEffect(() => () => {
    teardownChain();
    if (ctxRef.current && ctxRef.current.state !== 'closed') ctxRef.current.close();
  }, [teardownChain]);

  return {
    status, error, sourceLabel,
    startMic, startFile, startTestTone, startCamera, stop,
    tick, getFeatures,
    get sampleRate() { return ctxRef.current?.sampleRate ?? 0; },
  };
}

export default useAudioEngine;
